import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import dayjs from 'dayjs';

type StepValue =
  | null
  | PatientModel
  | AppointmentType
  | KocAilemResponse
  | InsuranceCompany
  // | HospitalDepartment
  | AppointmentUnit2
  // | FilteredDoctor
  | AppointmentDoctor
  | Slot;

interface Step {
  idx: number;
  label: TFuncKeyCommonNS;
  // selectedValue: null | { name: string; types: null | [] };
  value: StepValue;
  active: boolean;
  completed: boolean;
  hidden: boolean;
}
interface AppointmentState {
  activeStep: Step;
  steps: Step[];
  selected: {
    patient: PatientModel | null;
    appointmentType: AppointmentType | null;
    membershipInquiry: KocAilemResponse | null;
    insurance: InsuranceCompany | null;
    department: AppointmentUnit2 | null;
    // department: HospitalDepartment | null;
    // unit: AppointmentUnit | null;
    doctor: AppointmentDoctor | null;
    slot: Slot | null;
    startDate: string;
    endDate: string;
    day: string | null;
    dayWithSlots: DateWithSlots | null;
  };
  triggered: {
    nextStep: boolean;
    prevStep: boolean;
  };
}

const initialState: AppointmentState = {
  activeStep: {
    idx: 0,
    label: 'pet-mate',
    value: null,
    active: true,
    completed: false,
    hidden: false,
  },
  steps: [
    {
      idx: 0,
      label: 'pet-mate',
      value: null,
      active: true,
      completed: false,
      hidden: false,
    },
    {
      idx: 1,
      label: 'plan',
      value: null,
      active: false,
      completed: false,
      hidden: true,
    },  
    {
      idx: 2,
      label: 'service',
      value: null,
      active: false,
      completed: false,
      hidden: true,
    },
    {
      idx: 3,
      label: 'appointment-type',
      value: null,
      active: false,
      completed: false,
      hidden: false,
    },
    {
      idx: 4,
      label: 'membership-inquiry',
      value: null,
      active: false,
      completed: false,
      hidden: true,
    },
    {
      idx: 5,
      label: 'insurances',
      value: null,
      active: false,
      completed: false,
      hidden: true,
    },
    {
      idx: 6,
      label: 'department',
      value: null,
      active: false,
      completed: false,
      hidden: false,
    },
    {
      idx: 7,
      label: 'veterinarian',
      value: null,
      active: false,
      completed: false,
      hidden: false,
    },
    {
      idx: 8,
      label: 'date',
      value: null,
      active: false,
      completed: false,
      hidden: false,
    },
  ],
  selected: {
    patient: null,
    appointmentType: null,
    membershipInquiry: null,
    insurance: null,
    department: null,
    // unit: null,
    doctor: null,
    slot: null,
    startDate: dayjs().format(),
    endDate: dayjs().add(6, 'day').format(),
    day: null,
    dayWithSlots: null,
  },
  triggered: {
    nextStep: false,
    prevStep: false,
  },
};

export const appointmentSlice = createSlice({
  name: 'appointment',
  initialState,
  reducers: {
    setSelected: (state, action: PayloadAction<Partial<AppointmentState['selected']>>) => {
      state.selected = { ...state.selected, ...action.payload };

      const activeStepIndex = state.steps.findIndex((step) => step.active);
      // const currentStep = { ...state.steps[activeStepIndex] };

      activeStepIndex === 0
        ? (state.steps[activeStepIndex].value = state.selected.patient)
        : activeStepIndex === 1
        ? (state.steps[activeStepIndex].value = state.selected.plan)
        : activeStepIndex === 2
        ? (state.steps[activeStepIndex].value = state.selected.service)
        : activeStepIndex === 3
        ? (state.steps[activeStepIndex].value = state.selected.appointmentType)
        : activeStepIndex === 4
        ? (state.steps[activeStepIndex].value = state.selected.membershipInquiry)
        : activeStepIndex === 5
        ? (state.steps[activeStepIndex].value = state.selected.insurance)
        : activeStepIndex === 6
        ? (state.steps[activeStepIndex].value = state.selected.department)
        : activeStepIndex === 7
        ? (state.steps[activeStepIndex].value = state.selected.doctor)
        : activeStepIndex === 8
        ? (state.steps[activeStepIndex].value = state.selected.slot)
        : null;

      if (activeStepIndex !== -1) {
        state.activeStep = state.steps[activeStepIndex];
      }
    },
    setSteps: (state, action: PayloadAction<Step[]>) => {
      state.steps = action.payload;
    },
    setStep: (state, action: PayloadAction<Partial<Step>>) => {
      const stepIndex = state.steps.findIndex((step) => step.idx === action.payload.idx);
      if (stepIndex !== -1) {
        state.steps[stepIndex] = { ...state.steps[stepIndex], ...action.payload };
      }
    },
    resetSelected: (state) => {
      state.selected = initialState.selected;
    },
    resetSteps: (state) => {
      state.steps = initialState.steps;
    },
    resetAppointment: () => initialState,
    nextStep: (state) => {
      state.triggered.nextStep = true;
      const steps = state.steps;
      const activeStepIndex = steps.findIndex((step) => step.active);

      if (activeStepIndex !== -1) {
        const currentStep = { ...steps[activeStepIndex], active: false, completed: true };
        let nextStepIndex = -1;

        for (let i = activeStepIndex + 1; i < steps.length; i++) {
          if (!steps[i].hidden) {
            nextStepIndex = i;
            break;
          }
        }

        if (nextStepIndex !== -1) {
          const nextStep = { ...steps[nextStepIndex], active: true };
          const updatedSteps = [...steps];
          updatedSteps[activeStepIndex] = currentStep;
          updatedSteps[nextStepIndex] = nextStep;
          state.steps = updatedSteps;
          state.activeStep = nextStep;
        } else {
          const updatedSteps = [...steps];
          updatedSteps[activeStepIndex] = currentStep;
          state.steps = updatedSteps;
          state.activeStep = currentStep;
        }
      }
    },
    previousStep: (state) => {
      state.triggered.prevStep = true;
      const steps = state.steps;
      const activeStepIndex = steps.findIndex((step) => step.active);

      if (activeStepIndex > 0) {
        const currentStep = {
          ...steps[activeStepIndex],
          active: false,
          completed: false,
          value: null,
        };
        let previousStepIndex = -1;

        for (let i = activeStepIndex - 1; i >= 0; i--) {
          if (!steps[i].hidden) {
            previousStepIndex = i;
            break;
          }
        }

        if (previousStepIndex !== -1) {
          const previousStep = { ...steps[previousStepIndex], active: true };

          const updatedSteps = [...steps];
          updatedSteps[activeStepIndex] = currentStep;
          updatedSteps[previousStepIndex] = previousStep;
          state.steps = updatedSteps;
          state.activeStep = previousStep;
        }
      }
    },
    handleMonth: (state, action: PayloadAction<'prev' | 'next'>) => {
      if (action.payload === 'prev') {
        const firstDayOfPrevMonth = dayjs(state.selected.startDate)
          .subtract(1, 'month')
          .startOf('month');
        const isTodayBeforeFirstDayOfPrevMonth = dayjs().isBefore(dayjs(firstDayOfPrevMonth), 'day');

        if (isTodayBeforeFirstDayOfPrevMonth) {
          state.selected.day = firstDayOfPrevMonth.format();
          state.selected.startDate = firstDayOfPrevMonth.format();
          state.selected.endDate = firstDayOfPrevMonth.add(6, 'day').format();
        } else {
          state.selected.day = dayjs().format();
          state.selected.startDate = dayjs().format();
          state.selected.endDate = dayjs().add(6, 'day').format();
        }
      } else {
        const firstDayOfNextMonth = dayjs(state.selected.startDate).add(1, 'month').startOf('month');

        state.selected.day = firstDayOfNextMonth.format();
        state.selected.startDate = firstDayOfNextMonth.format();
        state.selected.endDate = firstDayOfNextMonth.add(6, 'day').format();
      }
    },
    handleWeek: (state, action: PayloadAction<'prev' | 'next'>) => {
      if (action.payload === 'prev') {
        const firstDayOfPrevWeek = dayjs(state.selected.startDate).subtract(1, 'week');
        const isTodayBeforeFirstDayOfPrevWeek = dayjs().isBefore(dayjs(firstDayOfPrevWeek), 'day');

        if (isTodayBeforeFirstDayOfPrevWeek) {
          state.selected.day = firstDayOfPrevWeek.format();
          state.selected.startDate = firstDayOfPrevWeek.format();
          state.selected.endDate = firstDayOfPrevWeek.add(6, 'day').format();
        } else {
          state.selected.day = dayjs().format();
          state.selected.startDate = dayjs().format();
          state.selected.endDate = dayjs().add(6, 'day').format();
        }
      } else {
        const firstDayOfNextWeek = dayjs(state.selected.startDate).add(1, 'week');

        state.selected.day = firstDayOfNextWeek.format();
        state.selected.startDate = firstDayOfNextWeek.format();
        state.selected.endDate = firstDayOfNextWeek.add(6, 'day').format();
      }
    },
  },
});

export const {
  setSelected,
  resetSelected,
  setSteps,
  setStep,
  resetSteps,
  resetAppointment,
  nextStep,
  previousStep,
  handleMonth,
  handleWeek,
} = appointmentSlice.actions;

export default appointmentSlice;

